/*
 * Copyright (c) Dito Technologies LLC
 */

import React from "react";
import styles from "ui/feature/piece-play/common/piece-renderer/PieceRendererStepThru.module.scss";
import classnames from "classnames";
import {createFragmentContainer} from "react-relay";
import {graphql} from "babel-plugin-relay/macro";
import {
    PieceRendererStepThru_piece$data
} from "ui/feature/piece-play/common/__generated__/PieceRendererStepThru_piece.graphql";
import PieceRendererStepThruNote from "ui/feature/piece-play/common/piece-renderer/PieceRendererStepThruNote";
import PieceRendererStepThruCurrentPlaceMarker
    from "ui/feature/piece-play/common/piece-renderer/PieceRendererStepThruCurrentPlaceMarker";
import {BulbIdToStateLookup} from "ui/feature/piece-play/common/PlayTypes";

interface Props {
    className?: string;
    currTime?: number;
    piece: PieceRendererStepThru_piece$data;
    bulbIdToStateLookup: BulbIdToStateLookup;
}

const PieceRendererStepThru: React.FC<Props> = ({currTime, className, piece, bulbIdToStateLookup}) => {

    const [maxTime, minMidi, maxMidi] = React.useMemo(() => {
        let currMaxTime = 0;
        let currMinMidi = null;
        let currMaxMidi = null;
        for (const note of piece.file?.notes || []) {
            const noteTime = note.note_start + note.note_duration;
            if (noteTime > currMaxTime) {
                currMaxTime = noteTime;
            }
            if (currMinMidi === null || currMaxMidi === null) {
                currMinMidi = note.midi_number;
                currMaxMidi = note.midi_number
            } else if (note.midi_number < currMinMidi) {
                currMinMidi = note.midi_number;
            } else if (note.midi_number > currMaxMidi) {
                currMaxMidi = note.midi_number
            }
        }
        return [currMaxTime, currMinMidi || 0, currMaxMidi || 100];
    }, [piece]);

    return (
        <div className={classnames(styles.container, className)}>
            {(piece.file?.notes || []).map((note) => (
                <PieceRendererStepThruNote
                    noteState={bulbIdToStateLookup[note.bulb_id] || "none"}
                    maxMidi={maxMidi}
                    maxTime={maxTime}
                    minMidi={minMidi}
                    key={note.bulb_id}
                    note={note}/>))}
            {currTime ? <PieceRendererStepThruCurrentPlaceMarker maxTime={maxTime} currTime={currTime}/> : null}
        </div>
    );
}

export default createFragmentContainer(PieceRendererStepThru, {
    piece: graphql`
    fragment PieceRendererStepThru_piece on MusicPiece {
        id
        file {
            notes {
                bulb_id
                note_duration
                note_start
                midi_number
                ...PieceRendererStepThruNote_note
            }
        }
    }
    `
});